// Roster dedup at subsequent-filing case-attach (SOR-219 — per-subcase duplicate
// GetCase party records).
//
// GetCase returns one party RECORD PER SUBCASE, so a logical party that appears
// across N subcases comes back as N occurrences sharing the same court
// participant id (externalId). OrderInfoTab.handleCaseSelected used to mint one
// wizard-local party (crypto.randomUUID) per occurrence and then
// `partyLocalIdByCourtId.set(externalId, id)` overwrote on each duplicate, so the
// court-id → local-id map kept only the LAST occurrence's id and the other N-1
// local copies became unlinked, orphaned parties. We fix it at the source: mint
// exactly one canonical wizard-local party per distinct logical party.
//
// Two occurrences are the same logical party when they share a non-empty court
// `referenceId` (the stable cross-subcase logical-party id), or — when NEITHER
// carries a referenceId — when they share a normalized name and role. The
// referenceId key takes precedence over the name fallback (SPEC-SOR-277-v1 R1).
// The per-subcase `externalId`/`primaryId` does NOT participate in the identity
// key: distinct per-subcase ids alone must not split one logical party (the
// MCV088999 defect — Monica's two records, primaryIds 977968 vs 978344, both
// referenceId 84136). The name fallback mirrors the `norm` / `partyNameKey` rules
// in `remap-metadata-references.ts` so the two reconcilers stay consistent for
// name matching; the role suffix on the name fallback keeps same-name /
// different-role parties separate without weakening the shared name key.
//
// On a court-filing roster an OVER-merge (two distinct people fused) is worse than
// an under-merge: referenceId eliminates over-merge for records that carry one
// (distinct non-empty referenceIds never collapse, even at identical name + role),
// and the name+role fallback fires ONLY when NEITHER record carries a referenceId.
//
// The first occurrence of each key is canonical, so the index-0 lead-client and
// the first occurrence's role-derived flags survive the collapse. Pure and
// side-effect-free: no crypto / Math.random, deterministic in input order.

export interface DedupPartyLike {
  /** Stable cross-subcase logical-party identity — the dedup key (referenceId-first). */
  referenceId?: string;
  /** Per-subcase court id (primaryId). Retained for downstream consumers; NOT part of the identity key. */
  externalId?: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  role: string;
}

// Mirrors remap-metadata-references.ts `norm` (collapse \s+, trim, lowercase).
const norm = (s: string | undefined): string =>
  (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

// Mirrors remap-metadata-references.ts `partyNameKey`: organization name wins,
// else first + last. The role suffix below is the dedup's addition (not part of
// this shared name key).
const partyNameKey = (p: DedupPartyLike): string =>
  norm(p.organizationName) || norm(`${p.firstName ?? ''} ${p.lastName ?? ''}`);

// referenceId-first; only when neither record carries one does the name+role
// fallback apply. The truthiness check treats an empty-string referenceId as "no
// referenceId" (falls through to the name fallback) rather than colliding on an
// empty key. The `ref:` / `name:` prefixes keep the two key spaces disjoint.
const dedupeKey = (p: DedupPartyLike): string =>
  p.referenceId ? `ref:${p.referenceId}` : `name:${partyNameKey(p)}|${norm(p.role)}`;

/**
 * A deduplicated roster paired with the full occurrence→canonical resolution.
 */
export interface GroupedPartyRoster<T> {
  /** One canonical party per distinct logical party, in first-occurrence order. */
  canonical: T[];
  /**
   * Maps EVERY input occurrence — including non-surviving duplicates — to its
   * canonical (first-occurrence) party, by object identity. This is what lets a
   * downstream consumer resolve a non-surviving occurrence's per-subcase id to the
   * canonical party it collapsed into; `canonical` alone discards that mapping.
   */
  resolution: Map<T, T>;
}

/**
 * Collapse duplicate GetCase party records to one canonical party per distinct
 * logical party, keyed referenceId-first then normalized name + role, AND return
 * the resolution from every input occurrence to its canonical party. The first
 * occurrence of each key is canonical, so lead-client / self-rep flags derived
 * from occurrence order are preserved.
 *
 * Deterministic: a pure function of input order, no randomness.
 */
export function dedupePartyRosterGrouped<T extends DedupPartyLike>(
  parties: T[],
): GroupedPartyRoster<T> {
  const canonicalByKey = new Map<string, T>();
  const canonical: T[] = [];
  const resolution = new Map<T, T>();
  for (const p of parties) {
    const key = dedupeKey(p);
    let lead = canonicalByKey.get(key);
    if (lead === undefined) {
      lead = p;
      canonicalByKey.set(key, p);
      canonical.push(p);
    }
    resolution.set(p, lead);
  }
  return { canonical, resolution };
}

/**
 * Collapse duplicate GetCase party records to one canonical party per distinct
 * logical party, keyed referenceId-first then normalized name + role. Returns the
 * canonical subset in first-occurrence order (first occurrence of each key wins),
 * so lead-client / self-rep flags derived from occurrence order are preserved.
 *
 * Idempotent and deterministic: a pure function of input order, no randomness.
 * Thin wrapper over {@link dedupePartyRosterGrouped} — the single source of truth
 * for the partition.
 */
export function dedupePartyRoster<T extends DedupPartyLike>(parties: T[]): T[] {
  return dedupePartyRosterGrouped(parties).canonical;
}
